import {Capacitor} from '@capacitor/core';
import {LocalNotifications} from '@capacitor/local-notifications';
import {isDymistrzLiveNotification} from './notification-plan.js';

function permissionState(value){
  return String(value||'prompt');
}

function atMillis(value){
  const raw=value?.schedule?.at;
  if(raw instanceof Date)return raw.getTime();
  const millis=Date.parse(String(raw||''));
  return Number.isNaN(millis)?null:millis;
}

function equivalent(existing,desired){
  return Number(existing?.id)===Number(desired?.id)
    &&String(existing?.title||'')===String(desired?.title||'')
    &&String(existing?.body||'')===String(desired?.body||'')
    &&atMillis(existing)===atMillis(desired)
    &&Boolean(existing?.schedule?.allowWhileIdle)===Boolean(desired?.schedule?.allowWhileIdle)
    &&Boolean(existing?.schedule?.isExactNotification)===Boolean(desired?.schedule?.isExactNotification)
    &&String(existing?.extra?.source||'')===String(desired?.extra?.source||'')
    &&String(existing?.extra?.route||'')===String(desired?.extra?.route||'')
    &&String(existing?.extra?.kind||'')===String(desired?.extra?.kind||'');
}

export function createLocalNotificationAdapter({
  plugin=LocalNotifications,
  platform=()=>Capacitor.getPlatform(),
  isOwned=isDymistrzLiveNotification
}={}){
  if(typeof isOwned!=='function')throw new TypeError('isOwned must be a function');
  if(!plugin)throw new TypeError('LocalNotifications plugin is required');

  function supported(){
    return ['android','ios'].includes(String(platform?.()||'').toLowerCase());
  }

  async function checkPermission(){
    if(!supported())return Object.freeze({supported:false,status:'unsupported'});
    const result=await plugin.checkPermissions();
    return Object.freeze({
      supported:true,
      status:permissionState(result?.display)
    });
  }

  async function requestPermission(){
    if(!supported())return Object.freeze({supported:false,status:'unsupported'});
    const result=await plugin.requestPermissions();
    return Object.freeze({
      supported:true,
      status:permissionState(result?.display)
    });
  }

  async function ensurePermission({request=false}={}){
    const current=await checkPermission();
    if(!current.supported||current.status==='granted'||!request)return current;
    return requestPermission();
  }

  async function pendingOwned(){
    if(!supported())return Object.freeze([]);
    const result=await plugin.getPending();
    return Object.freeze((result?.notifications||[]).filter(isOwned));
  }

  async function cancel(ids=[]){
    const descriptors=[...new Set((ids||[]).map(Number).filter(Number.isInteger))]
      .map(id=>Object.freeze({id}));
    if(!supported()||!descriptors.length)return Object.freeze({cancelled:0});
    await plugin.cancel({notifications:descriptors});
    return Object.freeze({cancelled:descriptors.length});
  }

  async function cancelOwned(){
    const pending=await pendingOwned();
    return cancel(pending.map(item=>item.id));
  }

  async function onAction(listener){
    if(typeof listener!=='function')throw new TypeError('notification action listener is required');
    if(!supported()||typeof plugin.addListener!=='function'){
      return Object.freeze({remove:async()=>{}});
    }
    const handle=await plugin.addListener('localNotificationActionPerformed',event=>{
      const notification=event?.notification;
      if(!isOwned(notification))return;
      const kind=String(notification?.extra?.kind||'').trim().toLowerCase();
      const actionId=String(event?.actionId||'tap').trim().toLowerCase();
      try{listener(Object.freeze({kind,action:actionId==='tap'?'tap':'other'}));}catch{}
    });
    return handle&&typeof handle.remove==='function'
      ?handle
      :Object.freeze({remove:async()=>{}});
  }

  async function reconcile(notifications=[]){
    if(!supported())return Object.freeze({status:'unsupported',scheduled:0,updated:0,cancelled:0});

    const desired=[...(notifications||[])];
    const permission=await ensurePermission({request:false});

    const current=await pendingOwned();
    const desiredById=new Map(desired.map(item=>[Number(item.id),item]));
    const currentById=new Map(current.map(item=>[Number(item.id),item]));

    const obsolete=current.filter(item=>!desiredById.has(Number(item.id)));
    if(obsolete.length)await cancel(obsolete.map(item=>item.id));

    if(permission.status!=='granted'){
      const retained=current.filter(item=>desiredById.has(Number(item.id)));
      if(retained.length)await cancel(retained.map(item=>item.id));
      return Object.freeze({
        status:'permission_required',
        permission:permission.status,
        scheduled:0,
        updated:0,
        cancelled:obsolete.length+retained.length
      });
    }

    const changed=desired.filter(item=>{
      const existing=currentById.get(Number(item.id));
      return existing&&!equivalent(existing,item);
    });
    const missing=desired.filter(item=>!currentById.has(Number(item.id)));

    if(changed.length)await plugin.update({notifications:changed});
    if(missing.length)await plugin.schedule({notifications:missing});

    return Object.freeze({
      status:'reconciled',
      permission:'granted',
      scheduled:missing.length,
      updated:changed.length,
      cancelled:obsolete.length,
      pending:desired.length
    });
  }

  return Object.freeze({
    supported,
    checkPermission,
    requestPermission,
    ensurePermission,
    pendingOwned,
    cancel,
    cancelOwned,
    onAction,
    reconcile
  });
}
